import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button } from "@material-tailwind/react";
import { MagnifyingGlassIcon, PencilSquareIcon, PlusCircleIcon, TrashIcon } from "@heroicons/react/24/outline";
import EmptyState from "../components/EmptyState";
import FormField from "../components/FormField";
import FormSelect from "../components/FormSelect";
import LoadingScreen from "../components/LoadingScreen";
import SectionCard from "../components/SectionCard";
import SegmentedControl from "../components/SegmentedControl";
import StatCard from "../components/StatCard";
import { useAuth } from "../context/AuthContext";
import { deleteTransaction, saveTransaction, subscribeByOwner } from "../services/db";
import { formatMoneyWithUnit, toEnglishDigits } from "../utils/format";
import { filterTransactionsByRange, getRanges, sortByDateDesc, sumTransactions } from "../utils/report";
import { ArrowTrendingDownIcon, ArrowTrendingUpIcon, BanknotesIcon } from "@heroicons/react/24/outline";

const initialForm = {
  title: "",
  category: "",
  type: "expense",
  amount: "",
  date: new Date().toISOString().slice(0, 10),
  notes: ""
};

export default function TransactionsPage() {
  const { user } = useAuth();
  const [transactions, setTransactions] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [editId, setEditId] = useState(null);
  const [queryText, setQueryText] = useState("");
  const [alert, setAlert] = useState(null);
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user?.uid) return undefined;
    const unsubscribe = subscribeByOwner("transactions", user.uid, setTransactions);
    const timer = setTimeout(() => setReady(true), 250);
    return () => {
      unsubscribe?.();
      clearTimeout(timer);
    };
  }, [user?.uid]);

  const ranges = useMemo(() => getRanges(), []);
  const today = useMemo(() => sumTransactions(filterTransactionsByRange(transactions, ranges.today.start, ranges.today.end)), [ranges.today.end, ranges.today.start, transactions]);
  const week = useMemo(() => sumTransactions(filterTransactionsByRange(transactions, ranges.week.start, ranges.week.end)), [ranges.week.end, ranges.week.start, transactions]);
  const month = useMemo(() => sumTransactions(filterTransactionsByRange(transactions, ranges.month.start, ranges.month.end)), [ranges.month.end, ranges.month.start, transactions]);

  const filtered = useMemo(() => {
    const text = queryText.trim().toLowerCase();
    return sortByDateDesc(transactions).filter((item) => {
      if (!text) return true;
      const haystack = `${item.title} ${item.category} ${item.notes} ${item.date}`.toLowerCase();
      return haystack.includes(text);
    });
  }, [queryText, transactions]);

  const resetForm = () => {
    setForm(initialForm);
    setEditId(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setAlert(null);
    try {
      setBusy(true);
      await saveTransaction({ ...form, ownerUid: user.uid }, editId);
      setAlert({ color: "green", message: editId ? "تم تعديل الحركة المالية بنجاح." : "تمت إضافة الحركة المالية بنجاح." });
      resetForm();
    } catch (err) {
      setAlert({ color: "red", message: err?.message || "حدث خطأ أثناء حفظ الحركة المالية." });
    } finally {
      setBusy(false);
    }
  };

  const handleEdit = (item) => {
    setEditId(item.id);
    setForm({
      title: item.title || "",
      category: item.category || "",
      type: item.type || "expense",
      amount: toEnglishDigits(String(item.amount || "")),
      date: item.date || new Date().toISOString().slice(0, 10),
      notes: item.notes || ""
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("هل تريد حذف هذه الحركة المالية؟")) return;
    try {
      await deleteTransaction(id);
      setAlert({ color: "green", message: "تم حذف الحركة المالية." });
    } catch {
      setAlert({ color: "red", message: "تعذر حذف الحركة المالية." });
    }
  };

  if (!ready) return <LoadingScreen text="جارٍ تحميل الحركات المالية..." />;

  return (
    <div className="page-shell space-y-4">
      <section className="mini-grid">
        <StatCard title="دخل اليوم" value={formatMoneyWithUnit(today.income)} icon={ArrowTrendingUpIcon} tone="success" />
        <StatCard title="مصروف الأسبوع" value={formatMoneyWithUnit(week.expense)} icon={ArrowTrendingDownIcon} tone="danger" />
        <StatCard title="صافي الشهر" value={formatMoneyWithUnit(month.net)} icon={BanknotesIcon} tone="brand" />
        <StatCard title="إجمالي العمليات" value={toEnglishDigits(filtered.length)} tone="accent" />
      </section>

      <SectionCard title={editId ? "تعديل حركة مالية" : "إضافة دخل أو مصروف"} subtitle="نموذج مريح وسريع ومناسب لشاشة الهاتف">
        {alert ? <Alert color={alert.color} className="mb-4 rounded-2xl text-sm">{alert.message}</Alert> : null}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <span className="field-label">نوع الحركة</span>
            <SegmentedControl
              value={form.type}
              onChange={(value) => setForm((prev) => ({ ...prev, type: value }))}
              options={[{ value: "income", label: "دخل" }, { value: "expense", label: "مصروف" }]}
            />
          </div>

          <div className="form-grid">
            <FormField label="العنوان" placeholder="مثال: دفعة مشروع أو إيجار" value={form.title} onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))} required />
            <FormField label="التصنيف" placeholder="مثال: مشروع - منزل - سيارة" value={form.category} onChange={(e) => setForm((prev) => ({ ...prev, category: e.target.value }))} />
            <FormField type="number" inputMode="decimal" dir="ltr" label="المبلغ" placeholder="0" value={form.amount} onChange={(e) => setForm((prev) => ({ ...prev, amount: e.target.value }))} required />
            <FormField type="date" dir="ltr" label="التاريخ" value={form.date} onChange={(e) => setForm((prev) => ({ ...prev, date: e.target.value }))} required />
            <div className="sm:col-span-2 xl:col-span-3">
              <FormField as="textarea" label="ملاحظات" placeholder="أي تفاصيل إضافية عن العملية..." value={form.notes} onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))} />
            </div>
          </div>

          <div className="flex flex-wrap gap-3">
            <Button type="submit" className="rounded-2xl bg-brand-700 px-5" disabled={busy}>
              <span className="flex items-center gap-2">
                <PlusCircleIcon className="h-5 w-5" />
                {editId ? "حفظ التعديل" : "إضافة الحركة"}
              </span>
            </Button>
            {editId ? (
              <Button type="button" variant="outlined" className="rounded-2xl border-brand-200 text-brand-700" onClick={resetForm}>
                إلغاء التعديل
              </Button>
            ) : null}
          </div>
        </form>
      </SectionCard>

      <SectionCard title="سجل الحركات المالية" subtitle="ابحث باسم العملية أو التصنيف أو التاريخ" action={<div className="relative w-full max-w-sm"><MagnifyingGlassIcon className="pointer-events-none absolute right-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" /><input className="field-input pr-11 input-rtl" placeholder="ابحث هنا" value={queryText} onChange={(e) => setQueryText(e.target.value)} /></div>}>
        {!filtered.length ? (
          <EmptyState title="لا توجد نتائج" description="أضف حركة مالية جديدة أو غيّر عبارة البحث." />
        ) : (
          <div className="space-y-3">
            {filtered.map((item) => (
              <div key={item.id} className="mobile-card">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="text-base font-extrabold text-slate-800 sm:text-lg">{item.title}</h3>
                      <span className={`rounded-full px-3 py-1 text-[11px] font-extrabold ${item.type === "income" ? "bg-emerald-50 text-income" : "bg-rose-50 text-expense"}`}>
                        {item.type === "income" ? "دخل" : "مصروف"}
                      </span>
                    </div>

                    <div className="mt-2 flex flex-wrap gap-3 text-xs text-slate-500 sm:text-sm">
                      <span>التصنيف: {item.category || "—"}</span>
                      <span>التاريخ: {item.date || "—"}</span>
                    </div>

                    {item.notes ? <p className="mt-3 text-sm leading-7 text-slate-600">{item.notes}</p> : null}
                  </div>

                  <div className="flex shrink-0 flex-col items-start gap-3 sm:items-end">
                    <div className={`text-lg font-extrabold sm:text-xl ${item.type === "income" ? "text-income" : "text-expense"}`}>
                      {item.type === "income" ? "+" : "-"}
                      {formatMoneyWithUnit(item.amount)}
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outlined" size="sm" className="flex items-center gap-2 rounded-xl border-brand-200 text-brand-700" onClick={() => handleEdit(item)}>
                        <PencilSquareIcon className="h-4 w-4" />
                        تعديل
                      </Button>
                      <Button variant="outlined" size="sm" className="flex items-center gap-2 rounded-xl border-red-200 text-red-600" onClick={() => handleDelete(item.id)}>
                        <TrashIcon className="h-4 w-4" />
                        حذف
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </SectionCard>
    </div>
  );
}
